import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Avatar, TextField, Button, Divider, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { getAuth, reauthenticateWithCredential, EmailAuthProvider, updatePassword } from 'firebase/auth';
import { getStoredUser } from '../../services/authService';
import { getAuditLogs, AuditLogItem } from '../../services/auditService';
import {
    rootSx,
    innerSx,
    headerBoxSx,
    titleSx,
    subtitleSx,
    columnsWrapperSx,
    leftColSx,
    paperSx,
    profileTitleSx,
    avatarContainerSx,
    avatarSx,
    cameraButtonSx,
    infoBlockSx,
    nameSx,
    emailSx,
    adminBadgeSx,
    adminIconSx,
    adminTextSx,
    fieldsRowSx,
    fieldColSx,
    fieldLabelSx,
    inputSx,
    disabledInputSx,
    saveButtonSx,
    changePaperSx,
    changeInnerSx,
    changeTitleSx,
    changeHelperSx,
    formSx,
    passwordFieldSx,
    updateButtonSx,
    recentPaperSx,
    recentTitleSx,
    activityRowSx,
    bulletSx
} from './MyAccount.styles';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SecurityOutlined from '@mui/icons-material/SecurityOutlined';

function formatActivityTime(timestamp: string | null | undefined): string {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return timestamp;
    const now = new Date();
    const todayStr = now.toDateString();
    const yest = new Date(now); yest.setDate(now.getDate() - 1);
    const hh = d.getHours().toString().padStart(2, '0');
    const mm = d.getMinutes().toString().padStart(2, '0');
    const time = `${hh}:${mm}`;
    if (d.toDateString() === todayStr) return `Today, ${time}`;
    if (d.toDateString() === yest.toDateString()) return `Yesterday, ${time}`;
    return `${d.toLocaleDateString()} ${time}`;
}

function formatActivityLabel(item: AuditLogItem): string {
    const parts: string[] = [];
    if (item.action) parts.push(item.action);
    if (item.target_entity) parts.push(item.target_entity);
    if (item.target_name) {
        parts.push(item.target_name);
    } else if (item.target_id && item.target_id !== String(item.id)) {
        parts.push(`#${item.target_id}`);
    }
    return parts.join(' ') || 'Activity';
}

const MyAccount: React.FC = () => {
    const user = getStoredUser();
    const displayName = user?.name ?? '';
    const displayEmail = user?.email ?? '';
    const displayRole = user?.roles?.[0] ?? 'Administrator';
    const nameParts = displayName.trim().split(/\s+/);
    const initials = nameParts.length >= 2
        ? (nameParts[0][0] + nameParts[1][0]).toUpperCase()
        : nameParts[0]?.substring(0, 2).toUpperCase() ?? 'A';

    const [avatarSrc, setAvatarSrc] = useState<string | null>(null);
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [currentPass, setCurrentPass] = useState('');
    const [newPass, setNewPass] = useState('');
    const [confirmPass, setConfirmPass] = useState('');
    const [confirmError, setConfirmError] = useState('');
    const [pwLoading, setPwLoading] = useState(false);
    const [pwSuccess, setPwSuccess] = useState('');
    const [pwError, setPwError] = useState('');
    const [recentLogs, setRecentLogs] = useState<AuditLogItem[]>([]);

    useEffect(() => {
        if (!user?.id) return;
        getAuditLogs(1, 5, undefined, undefined, undefined, user.id)
            .then(({ logs }) => setRecentLogs(logs))
            .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePasswordUpdate = async () => {
        if (newPass !== confirmPass) {
            setConfirmError('Passwords do not match');
            return;
        }
        if (!newPass) return;
        setPwLoading(true);
        setPwError('');
        setPwSuccess('');
        setConfirmError('');
        try {
            const auth = getAuth();
            const firebaseUser = auth.currentUser;
            if (!firebaseUser || !firebaseUser.email) throw new Error('Not authenticated');
            const credential = EmailAuthProvider.credential(firebaseUser.email, currentPass);
            await reauthenticateWithCredential(firebaseUser, credential);
            await updatePassword(firebaseUser, newPass);
            setPwSuccess('Password updated successfully.');
            setCurrentPass('');
            setNewPass('');
            setConfirmPass('');
        } catch (err: any) {
            const code: string = err?.code ?? '';
            if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
                setPwError('Current password is incorrect.');
            } else if (code === 'auth/weak-password') {
                setPwError('New password is too weak. Use at least 6 characters.');
            } else if (code === 'auth/requires-recent-login') {
                setPwError('Session expired. Please log out and log in again.');
            } else {
                setPwError(err?.message ?? 'Failed to update password.');
            }
        } finally {
            setPwLoading(false);
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setAvatarSrc(reader.result as string);
        };
        reader.readAsDataURL(file);
        // reset input so same file can be re-selected later
        e.currentTarget.value = '';
    };

    return (
        <Box sx={rootSx}>
            <Box sx={innerSx}>
                <Box sx={headerBoxSx}>
                    <Typography sx={titleSx}>My Account</Typography>
                    <Typography sx={subtitleSx}>Manage your profile, security settings and preferences.</Typography>
                </Box>

                <Box sx={columnsWrapperSx}>
                    <Box sx={leftColSx}>
                        <Paper sx={paperSx}>
                            <Typography sx={profileTitleSx}>Profile</Typography>
                            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <Box sx={avatarContainerSx}>
                                    {/* KAN-139: always render initials as children so the Avatar
                                        falls back to them not only when the URL is missing but also
                                        when the image fails to load (e.g. expired/invalid presigned URL). */}
                                    <Avatar src={avatarSrc ?? undefined} sx={avatarSx}>{initials}</Avatar>
                                    <IconButton aria-label="upload picture" component="label" size="small" sx={cameraButtonSx}>
                                        <input hidden accept="image/*" type="file" onChange={handleImageChange} />
                                        <PhotoCamera sx={{ width: 11, height: 11 }} />
                                    </IconButton>
                                </Box>
                                <Box sx={infoBlockSx}>
                                    <Typography sx={nameSx}>{displayName}</Typography>
                                    <Typography sx={emailSx}>{displayEmail}</Typography>
                                    <Box sx={adminBadgeSx}>
                                        <SecurityOutlined sx={adminIconSx} />
                                        <Typography sx={adminTextSx}>{displayRole}</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={fieldsRowSx}>
                                <Box sx={fieldColSx(272.5)}>
                                    <Typography sx={fieldLabelSx}>Full name</Typography>
                                    <TextField size="small" defaultValue={displayName} sx={[inputSx, { width: 272.5 }]} />
                                </Box>

                                <Box sx={fieldColSx(272.5)}>
                                    <Typography sx={fieldLabelSx}>Role</Typography>
                                    <TextField size="small" defaultValue={displayRole} disabled sx={[inputSx, { width: 272.5 }, disabledInputSx]} InputProps={{ readOnly: true }} />
                                </Box>
                            </Box>

                            <Box sx={{ width: '100%', mt: 3 }}>
                                <Typography sx={fieldLabelSx}>Email address</Typography>
                                <TextField size="small" defaultValue={displayEmail} disabled sx={inputSx} />
                            </Box>

                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                <Button variant="contained" sx={saveButtonSx}>Save profile</Button>
                            </Box>
                        </Paper>

                        <Paper sx={{ boxSizing: 'border-box', display: 'flex', flexDirection: 'column', width: '100%', background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '14px' }}>
                            <Box sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '14px', lineHeight: '21px', letterSpacing: '-0.15px' }}>Change Password</Typography>
                                </Box>
                                <Typography sx={{ fontSize: 12, color: '#9CA3AF', mb: 0, mt: 1 }}>Choose a strong password you don't use elsewhere.</Typography>

                                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 0, width: '100%', mt: 2 }}>
                                    <Box>
                                        <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', letterSpacing: '-0.08px', color: '#111827', mb: 0 }}>Current password</Typography>
                                        <TextField
                                            size="small"
                                            placeholder="Enter current password"
                                            type={showCurrent ? 'text' : 'password'}
                                            sx={{
                                                width: '100%',
                                                '& .MuiOutlinedInput-root': {
                                                    height: '41.5px',
                                                    borderRadius: '10px',
                                                    '& fieldset': { borderWidth: '1px' }
                                                },
                                                '& .MuiOutlinedInput-input': { padding: '10px 12px' }
                                            }}
                                            value={currentPass}
                                            onChange={e => setCurrentPass(e.target.value)}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label={showCurrent ? 'Hide password' : 'Show password'}
                                                            onClick={() => setShowCurrent(s => !s)}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showCurrent ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{ mt: 2 }}>
                                        <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', letterSpacing: '-0.08px', color: '#111827', mb: 0 }}>New password</Typography>
                                        <TextField
                                            size="small"
                                            placeholder="Enter new password"
                                            type={showNew ? 'text' : 'password'}
                                            sx={{
                                                width: '100%',
                                                '& .MuiOutlinedInput-root': {
                                                    height: '41.5px',
                                                    borderRadius: '10px',
                                                    '& fieldset': { borderWidth: '1px' }
                                                },
                                                '& .MuiOutlinedInput-input': { padding: '10px 12px' }
                                            }}
                                            value={newPass}
                                            onChange={e => {
                                                const v = e.target.value;
                                                setNewPass(v);
                                                // live-validate against confirm field
                                                if (confirmPass && v !== confirmPass) {
                                                    setConfirmError('Passwords do not match');
                                                } else {
                                                    setConfirmError('');
                                                }
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label={showNew ? 'Hide password' : 'Show password'}
                                                            onClick={() => setShowNew(s => !s)}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{ mt: 2 }}>
                                        <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', letterSpacing: '-0.08px', color: '#111827', mb: 0 }}>Confirm new password</Typography>
                                        <Tooltip title={confirmError || ''} open={!!confirmError} placement="bottom-start" arrow>
                                            <TextField
                                                size="small"
                                                placeholder="Confirm new password"
                                                type={showConfirm ? 'text' : 'password'}
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiOutlinedInput-root': {
                                                        height: '41.5px',
                                                        borderRadius: '10px',
                                                        '& fieldset': { borderWidth: '1px' }
                                                    },
                                                    '& .MuiOutlinedInput-input': { padding: '10px 12px' }
                                                }}
                                                value={confirmPass}
                                                onChange={e => {
                                                    const v = e.target.value;
                                                    setConfirmPass(v);
                                                    if (v && v !== newPass) {
                                                        setConfirmError('Passwords do not match');
                                                    } else {
                                                        setConfirmError('');
                                                    }
                                                }}
                                                error={!!confirmError}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                                                                onClick={() => setShowConfirm(s => !s)}
                                                                edge="end"
                                                                size="small"
                                                            >
                                                                {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>

                                    {pwError && (
                                        <Typography sx={{ fontSize: 12, color: '#DC2626', mt: 1 }}>{pwError}</Typography>
                                    )}
                                    {pwSuccess && (
                                        <Typography sx={{ fontSize: 12, color: '#16A34A', mt: 1 }}>{pwSuccess}</Typography>
                                    )}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                        <Button
                                            variant="contained"
                                            disabled={pwLoading}
                                            sx={{
                                                background: '#39A9B1',
                                                color: '#FFFFFF',
                                                borderRadius: '10px',
                                                textTransform: 'none',
                                                fontFamily: 'Inter, Arial, sans-serif',
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                lineHeight: '19.5px',
                                                letterSpacing: '-0.08px',
                                                textAlign: 'center',
                                                px: '20px',
                                                py: '10px'
                                            }}
                                            onClick={handlePasswordUpdate}
                                        >
                                            {pwLoading ? 'Updating...' : 'Update password'}
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 16, width: 264 }}>
                        <Paper sx={recentPaperSx}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0 }}>
                                <Typography sx={recentTitleSx}>Recent Activity</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                {recentLogs.length === 0 ? (
                                    <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>No recent activity.</Typography>
                                ) : recentLogs.map((item, idx) => (
                                    <React.Fragment key={item.id ?? idx}>
                                        {idx > 0 && <Divider sx={{ my: 0 }} />}
                                        <Box sx={activityRowSx}>
                                            <Box sx={bulletSx} />
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography sx={{ fontSize: 12, color: '#374151' }}>{formatActivityLabel(item)}</Typography>
                                                <Typography sx={{ fontSize: 10, color: '#9CA3AF' }}>{formatActivityTime(item.timestamp)}</Typography>
                                            </Box>
                                        </Box>
                                    </React.Fragment>
                                ))}
                            </Box>
                        </Paper>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MyAccount;
